import { useCallback, useState } from 'react';

import useLocalStorage from '@/hooks/useLocalStorage';
import { trackEvent } from '@/segment/segment';
import { languageByCountryCode } from '@/shared/languageCodes';
import { EventName } from '@/shared/types/segment';
import { formatLanguageLabel, prioritizeLanguageOptions } from '@/util/languageOptions';

export const useTranslationState = (sessionId?: string) => {
  const [storedLanguage, setStoredLanguage] = useLocalStorage(
    'tryItTranslationLanguage',
    undefined
  );
  const [selectedLanguage, setSelectedLanguage] = useState<string | undefined>(storedLanguage);
  const [isEnabled, setIsEnabled] = useState(false);

  const languageOptions = prioritizeLanguageOptions(
    Object.keys(languageByCountryCode).map((i) => ({ value: i, label: formatLanguageLabel(i) }))
  );

  const currentLanguage = isEnabled ? selectedLanguage : undefined;

  const handleToggle = useCallback(() => {
    setIsEnabled((prev) => !prev);
  }, []);

  const handleLanguageSelect = useCallback(
    (newValue: string) => {
      setSelectedLanguage(newValue || undefined);
      setStoredLanguage(newValue || undefined);

      if (newValue) {
        trackEvent(EventName.ClickConversationTranslate, {
          session_id: sessionId,
          language: newValue,
        });
      }

      if (newValue && !isEnabled) {
        setIsEnabled(true);
      } else if (!newValue && isEnabled) {
        setIsEnabled(false);
      }
    },
    [isEnabled, setStoredLanguage, sessionId]
  );

  const disableOnError = useCallback((isError: boolean) => {
    if (isError) {
      setIsEnabled(false);
    }
  }, []);

  return {
    isEnabled,
    selectedLanguage,
    currentLanguage,
    languageOptions,
    handleToggle,
    handleLanguageSelect,
    disableOnError,
  };
};
